import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { InteriorSlide, SlideBanner } from './SlideShell';
import type { SlideProps } from './SlideShell';
const SNAK_WALKTHROUGH = 'https://cdn.magicpatterns.com/uploads/h7bdnvhxLhSgFubkvT1rw7/Screenshot_2026-09-21_at_11.48.50.png';
const PRINCIPLES = ['Short enough to finish.', 'Interesting enough to continue.', 'Interactive enough to learn from.'] as const;
const ARC = ['Story', 'Choice', 'Insight', 'Action'] as const;
export function SlideAttentionProblem({}: SlideProps) {
  return <InteriorSlide surface banner={<SlideBanner data-id="element-37" data-mp="27sxxnmrbgeff">Every interaction creates a signal.</SlideBanner>} data-id="element-36" data-mp="27usgmvj11wgv">
      <div className="flex flex-shrink-0 flex-col gap-[clamp(1rem,2vw,2rem)] lg:flex-row lg:items-start lg:justify-between lg:gap-12" data-id="element-38" data-mp="2clime9vd81c3">
        <div className="min-w-0 lg:max-w-[42%]" data-id="element-39" data-mp="1f5lbhdp2i8mn">
          <motion.p initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.45
        }} className="mb-3 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-40" data-mp="1k4en10ksnpk6">
            The attention problem
          </motion.p>

          <motion.h2 initial={{
          opacity: 0,
          y: 18
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.12,
          duration: 0.55
        }} className="text-[clamp(1.8rem,3.4vw,3.2rem)] font-extrabold uppercase leading-[0.98] tracking-[-0.045em] text-[var(--fg)]" data-id="element-41" data-mp="3iu394indhh9k">
            HCPs don’t lack content.
            <span className="mt-1 block text-[var(--brand)]" data-id="element-42" data-mp="2zwiip74ymamu">
              They lack time.
            </span>
          </motion.h2>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.28,
        duration: 0.5
      }} className="max-w-2xl flex-shrink-0 border-l-2 border-[var(--brand)] pl-5" data-id="element-43" data-mp="2vwrmgnwnz2ax">
          <p className="text-[clamp(0.65rem,0.9vw,0.8rem)] font-bold uppercase tracking-[0.16em] text-[var(--muted)]" data-id="element-44" data-mp="2nmsvvtsmuqbf">
            So we created the Snak.
          </p>
          <p className="mt-1.5 text-[clamp(1.15rem,1.8vw,1.75rem)] font-extrabold leading-tight tracking-[-0.025em] text-[var(--fg)]" data-id="element-45" data-mp="2sfcc6yipfzjx">
            A short story you participate in.
          </p>
          <p className="mt-[clamp(0.5rem,0.9vw,0.8rem)] text-[clamp(0.8rem,1.05vw,1rem)] leading-relaxed text-[var(--muted)]" data-id="element-46" data-mp="2ildbm3ya4nu3">
            A Snak turns complex content into a short, engaging journey. HCPs
            don’t just read or watch — they choose, react and discover. Every
            interaction creates insight.
          </p>
          <ul className="mt-[clamp(0.6rem,1.1vw,1rem)] flex flex-wrap gap-x-[clamp(1rem,2vw,2rem)] gap-y-2" data-id="element-47" data-mp="32b712bdiuqv5">
            {PRINCIPLES.map((principle, index) => <li key={principle} className="flex items-center gap-2" data-id="element-48" data-mp="1lza22fhdaere" data-mpi={index}>
                <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[var(--brand)] text-[10px] font-bold text-white" data-id="element-49" data-mp="1qx1qv0mptkrd">
                  {index + 1}
                </span>
                <span className="text-[clamp(0.78rem,1vw,0.95rem)] font-semibold text-[var(--fg)]" data-id="element-50" data-mp="1ed4r8nxt1995">
                  {principle}
                </span>
              </li>)}
          </ul>
        </motion.div>
      </div>

      <motion.figure initial={{
      opacity: 0,
      y: 26
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.4,
      duration: 0.6
    }} className="mt-[clamp(1rem,2vw,1.75rem)] flex min-h-0 flex-1 flex-col" data-id="element-51" data-mp="1ncjjnsxj7twa">
        <figcaption className="mb-[clamp(0.4rem,0.8vw,0.7rem)] flex-shrink-0 text-[clamp(0.58rem,0.8vw,0.72rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-52" data-mp="19euu47sirykw">
          A Snak journey — one short story, screen by screen
        </figcaption>

        <div className="flex min-h-0 flex-1 items-center justify-center" data-id="element-53" data-mp="gvxnpi3fb0kg">
          <div className="relative aspect-[3.06/1] max-h-full w-full overflow-hidden rounded-[1.5rem] bg-white shadow-sm ring-1 ring-[var(--fg)]/5" data-id="element-54" data-mp="bynzu6vomwyq">
            <img src={SNAK_WALKTHROUGH} alt="Five consecutive screens from an interactive Snak journey" className="absolute left-0 top-0 w-full -translate-y-[16%]" data-id="element-55" data-mp="3gc1fmjnvn48l" />
          </div>
        </div>

        <div className="mt-[clamp(0.6rem,1.1vw,1rem)] flex flex-shrink-0 items-center justify-center gap-[clamp(0.5rem,1.2vw,1.25rem)]" data-id="element-56" data-mp="3p666h330gfo4">
          {ARC.map((step, index) => <React.Fragment key={step} data-id="element-57" data-mp="2wbtk1579t9v" data-mpi={index}>
              {index > 0 && <ArrowRightIcon className="h-[clamp(0.85rem,1.1vw,1.1rem)] w-[clamp(0.85rem,1.1vw,1.1rem)] flex-shrink-0 text-[var(--brand)]" aria-hidden="true" data-id="element-58" data-mp="7vyaolo5ftny" />}
              <span className="text-[clamp(0.75rem,1.05vw,1rem)] font-extrabold uppercase tracking-[0.18em] text-[var(--fg)]" data-id="element-59" data-mp="29p5ykjhtvjlg">
                {step}
              </span>
            </React.Fragment>)}
        </div>
      </motion.figure>
    </InteriorSlide>;
}